import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { apiRequest, formatDate, toast } from "@/Helpers/general";
import TextareaInputTemplate from "@/Components/Templates/Forms/TextareaInputTemplate";
import { fieldsPlaceholders } from "@/Config/forms";
import { Button } from "primereact/button";
import { getUserAvatar } from "@/Helpers/user";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import { IL_ROWS_NO } from "@/Config/constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { AppContext } from "@/Components/Context/AppContext";
import DateInputTemplate from "@/Components/Templates/Forms/DateInputTemplate";
import { InputSwitch } from "primereact/inputswitch";
import ConfirmDialogTemplate from "@/Components/Templates/ConfirmDialogTemplate";
import moment from "moment";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import { Tooltip } from 'primereact/tooltip';
import { RequestsContext } from "@/Components/Context/RequestsContext";

const NotesWrap = styled.div`
    .notes-list-wrap {
        border-right: 1px solid var(--gray-200);
        padding-right: 10px;

        .note-item-wrap {
            padding: 5px 10px;  
            opacity: 0.7;
            cursor: pointer;
            border-radius: 4px;

            &:hover, &.active {
                opacity: 1;
                background-color: var(--gray-200);
            }   
            
            .note-user, .note-date, .note-desc {
                font-size: 13px;
            }

            .note-user {
                font-weight: bold;
            }

            .note-desc {
                height: 1.5rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }        
    }

    .note-preview {
        .user-info {
            .user-name {
                font-weight: bold;
            }

            .note-date {
                font-size: 13px;
                color: var(--gray-700);
            }
        }

        .note-description {
            background: var(--gray-100);
            padding: 0.5rem 1rem 1rem 1rem;
            border-radius: 4px;
        }
    }

    .add-note-wrap {
        border-top: 1px solid var(--gray-200);
        margin-top: 10px;
        .confirmButton {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: flex-end;
            margin-top:26px;
        }
    }

    .next-action {
        background: var(--gray-100);
        border-radius: 4px;
        padding: 0.5rem 1rem 0.5rem 1rem;
        opacity: 0.4;
        transition: opacity 0.3s ease;
        pointer-events: none;
    }

    .next-action.visible {
        opacity: 1;
        pointer-events: auto;
    }
    
    .loader-wrap {
        position: relative;
        height: 60px;
    }

    .no-notes {
        padding: 1.5rem;
    }
`;

/**
 * Component which renders notes related functionality (listing/adding note)
 */
const Notes = ({ caseId, disabled }) => {
    const { toastRef } = useContext(AppContext);
    const { statusOptions } = useContext(RequestsContext);

    const [notes, setNotes] = useState([]);
    const [actionStatus, setActionStatus] = useState(false);
    const [nextActionDate, setNextActionDate] = useState(null);
    const [nextActionNote, setNextActionNote] = useState('');
    const [nextActionStatus, setNextActionStatus] = useState('');
    const [newNote, setNewNote] = useState('');
    const [selectedNoteId, setSelectedNoteId] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [showNextActionChangeDialog, setShowNextActionChangeDialog] = useState();
    const [nextActionLoading, setNextActionLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreNotes, setHasMoreNotes] = useState(true);
    const [triggerRefreshList, setTriggerRefreshList] = useState();
    const [triggerRefreshValues, setTriggerRefreshValues] = useState();
    const [isAssigned, setIsAssigned] = useState(false);


    /**
     * Generate a preview of a note
     */
    const generateNotePreview = () => {
        let getCurrentNote = notes?.find((note) => note.id == selectedNoteId);
        // console.log(getCurrentNote);

        if (getCurrentNote) {
            return (<>
                <div className="user-info mb-3 flex">
                    {getUserAvatar(getCurrentNote.user)}

                    <div>
                        <div className="user-name">{getCurrentNote?.user?.name}</div>
                        <div className="note-date">{formatDate(getCurrentNote.created_at)}</div>
                    </div>
                </div>

                <div className="note-description">{getCurrentNote.description}</div>
            </>);
        }
    };

    /**
     * Add a new note
     */
    const submitForm = async () => {
        if (newNote != '') {
            setFormLoading(true);

            apiRequest('post', 'case/' + caseId + '/note', {
                data: {
                    description: newNote
                },
                onSuccess: (response) => {
                    setFormLoading(false);
                    setNewNote('');

                    // refresh list
                    setNotes([]);
                    setSelectedNoteId('');
                    setCurrentPage(1);
                    setHasMoreNotes(true);
                    setTriggerRefreshList(Date.now());

                    toast(toastRef, 'success', 'The note has been saved.');
                },
                onFailure: (error) => {
                    setFormLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            toast(toastRef, 'error', 'Error saving note.');

                        } else {
                            toast(toastRef, 'error', 'Error saving note.');
                        }

                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error saving note.');
                    }
                }
            });
        }
    };

    /**
     * Get notes from the API
     */
    const loadNotes = async () => {
        let requestParams = {
            page: currentPage,
            limit: IL_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/notes', {
            data: requestParams,
            onSuccess: (response) => {
                if (response.result && response.result.length > 0) {
                    setNotes([...notes, ...response.result]);

                    if (currentPage == 1) {
                        setSelectedNoteId(response.result[0].id);
                    }

                    if (currentPage == response.pagination.totalPages) {
                        setHasMoreNotes(false);
                    }

                    setCurrentPage(currentPage + 1);

                } else {
                    setHasMoreNotes(false);
                }
            },
            onFailure: () => {
                setHasMoreNotes(false);
                toast(toastRef, 'error', 'Notes fields loading failed.');
            },
        });
    };

    const loadNextActionData = async () => {
        apiRequest('get', 'case/' + caseId, {
            data: {},
            onSuccess: (response) => {
                if (response.result) {
                    setNextActionNote(response.result.request?.next_action_note || '');
                    setActionStatus(response.result.request?.next_action_status);
                    setNextActionStatus(response.result.request?.next_action_status2);
                    if(response.result.user_id){
                        setIsAssigned(true);
                    }
                    if (response.result.request?.next_action_date) {
                        setNextActionDate(moment(response.result.request?.next_action_date).toDate());
                    }
                    else {
                        setNextActionDate(null);
                    }
                }
            },
            onFailure: () => {
                toast(toastRef, 'error', 'Next action fields loading failed.');
            }
        });
    }

    /**
     * Update next action status to the API
     */
    const submitNextActionStatus = async (status) => {
        apiRequest('post', 'case/' + caseId + '/next-action-status', {
            data: {
                status: status,
            },
            onSuccess: (response) => {
                setNextActionLoading(false);
                setTriggerRefreshValues(Date.now());
                if (status) {
                    toast(toastRef, 'success', 'Next action has been enabled.');
                } else {
                    toast(toastRef, 'success', 'Next action has been disabled.');
                }
            },
            onFailure: (error) => {
                setNextActionLoading(false);

                if (error.status == 422) {
                    if (error.messages) {
                        let errorMessages = error.messages,
                            newFormErrors = {};
                        for (let key in errorMessages) {
                            if (Object.hasOwnProperty.call(errorMessages, key)) {
                                newFormErrors[key] = errorMessages[key];
                            }
                        }
                        toast(toastRef, 'error', 'Error saving note.');
                    } else {
                        toast(toastRef, 'error', 'Error saving note.');
                    }
                } else if (error.status == 403) {
                    toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');
                } else {
                    toast(toastRef, 'error', 'Error saving bext action.');
                }
            },
        });
    };
    /**
     * Update next action fields to the API
     */
    const submitNextActionFields = async () => {
        if (actionStatus) {
            setNextActionLoading(true);
            apiRequest('post', 'case/' + caseId + '/next-action', {
                data: {
                    next_action_date: nextActionDate,
                    next_action_note: nextActionNote,
                    next_action_status2: nextActionStatus,
                },
                onSuccess: (response) => {
                    setTriggerRefreshValues(Date.now());
                    setNextActionLoading(false);
                    toast(toastRef, 'success', 'The next action fields have been updated.');
                },
                onFailure: (error) => {
                    setNextActionLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};
                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }
                            toast(toastRef, 'error', 'Error saving note.');
                        } else {
                            toast(toastRef, 'error', 'Error saving note.');
                        }
                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');
                    } else {
                        toast(toastRef, 'error', 'Error saving next action fields.');
                    }
                },
            });
        }
    };

    /**
     * Get the next page
     */
    const loadNextPage = () => {
        if (hasMoreNotes) {
            loadNotes();
        }
    };

    // refresh notes list
    useEffect(() => {
        if (triggerRefreshList) {
            loadNotes();
        }
    }, [triggerRefreshList]);

    // refresh next action fields
    useEffect(() => {
        if (triggerRefreshValues) {
            loadNextActionData();
        }
    }, [triggerRefreshValues]);

    // init
    useEffect(() => {
        loadNotes();
        loadNextActionData();
    }, []);

    return (
        <NotesWrap>
            <div className="grid">
                <div className="col-6">
                    <div className="notes-list-wrap">
                        <InfiniteScroll
                            height="48vh"
                            dataLength={notes.length}
                            next={loadNextPage}
                            hasMore={hasMoreNotes}
                            loader={
                                <div className="loader-wrap">
                                    <ContentLoadingTemplate />
                                </div>
                            }
                            endMessage={notes.length == 0 && <div className="no-notes">No notes found.</div>}
                        >
                            {notes.map((note) => {
                                return (
                                    <div key={note.id} className={'note-item-wrap' + (selectedNoteId == note.id ? ' active' : '')} onClick={() => setSelectedNoteId(note.id)}>
                                        <div className="flex justify-content-between">
                                            <span className="note-user">{note.user.name}</span>
                                            <span className="note-date">{formatDate(note.created_at)}</span>
                                        </div>

                                        <div className="note-desc">{note.description}</div>
                                    </div>
                                );
                            })}
                        </InfiniteScroll>
                    </div>
                </div>

                <div className="col-6">
                    <div className="note-preview">{generateNotePreview()}</div>
                </div>
            </div>
            {!disabled && (
                <div className="grid add-note-wrap">
                    <div className="col-6"></div>
                    <div className="col-6">
                        <div className="flex flex-row align-items-center">
                            {!isAssigned && <Tooltip target="#disabled-switch" content={'The case needs to be assigned in order to change the next action'} position="top" />}
                            <div className="flex flex-row align-items-center" id="disabled-switch">
                                <InputSwitch
                                    style={{ scale: '70%' }}
                                    className="mr-2"
                                    tooltipOptions={{ showOnDisabled: true }}
                                    disabled={!isAssigned}
                                    checked={actionStatus && isAssigned}
                                    onChange={(e) => {
                                        if (actionStatus) {
                                            setShowNextActionChangeDialog(true);
                                        } else {
                                            setActionStatus(!actionStatus);
                                            submitNextActionStatus(!actionStatus);
                                        }
                                    }}
                                />
                                <label
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        if (isAssigned) {
                                            if (actionStatus) {
                                                setShowNextActionChangeDialog(true);
                                            } else {
                                                setActionStatus(!actionStatus);
                                                submitNextActionStatus(!actionStatus);
                                            }
                                        }
                                    }}
                                >
                                    Next Action
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 pb-3 pr-5 flex flex-column justify-content-between">
                        <TextareaInputTemplate name="new-note" label="Add a new note" bottomMargin={2} placeholder={fieldsPlaceholders.text} value={newNote} onChangeFunction={(name, value) => setNewNote(value)} />
                        <div className="confirmButton">
                            <Button className="mb-1" label="Save" onClick={submitForm} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
                        </div>
                    </div>

                    <div className={`col-6 pb-3 flex flex-column justify-content-between next-action ${actionStatus && isAssigned ? 'visible' : ''}`}>
                        <TextareaInputTemplate name="next-action-note" disabled={!(actionStatus && isAssigned)} label="Next Action Note" bottomMargin={2} placeholder={fieldsPlaceholders.text} value={nextActionNote} onChangeFunction={(name, value) => setNextActionNote(value)} />

                        <div className="flex flex-row justify-content-between align-items-end">
                            <DateInputTemplate name="next-action-date" disabled={!(actionStatus && isAssigned)} label="Next Action Date" bottomMargin={1} placeholder={fieldsPlaceholders.date} value={nextActionDate} onChangeFunction={(name, value) => setNextActionDate(value)} />
                            <DropdownInputTemplate name="next-action-date" disabled={!(actionStatus && isAssigned)} label="Next Action Status" bottomMargin={1} placeholder={fieldsPlaceholders.boolean} value={nextActionStatus} options={statusOptions} onChangeFunction={(name, value) => setNextActionStatus(value)} />
                            <div className="confirmButton">
                                <Button className="mb-1" label="Update" onClick={submitNextActionFields} loading={nextActionLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialogTemplate
                title="Disable Next Action"
                message={'Are you sure you wish to disable the next action? All data related to the next action will be removed.'}
                onConfirm={() => {
                    setActionStatus(!actionStatus);
                    submitNextActionStatus(!actionStatus);
                    setShowNextActionChangeDialog(false);
                }}
                visible={showNextActionChangeDialog}
                setVisible={setShowNextActionChangeDialog}
            />
        </NotesWrap>
    );
};

export default Notes;
